<template>
  <div>
    <ValidationObserver ref="observer">
      <div v-if="!isEdit && !isView">
        <h1 v-bg-typography="'h1'">Create Website Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for creating a test.
          <a
            href="https://braingineers.crunch.help/dashboard-faq/create-a-new-test"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <div v-if="isEdit">
        <h1 v-bg-typography="'h1'">Edit Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for Editing a test.
          <a
            href="https://braingineers.crunch.help/dashboard-faq/create-a-new-test"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <div v-if="isView">
        <h1 v-bg-typography="'h1'">View Test</h1>
        <p v-bg-typography="'body-m'">
          This form is for viewing a test. If you want to know more about the
          next steps.
          <a
            href="https://braingineers.crunch.help/faq/research-steps-and-timelines"
            target="_blank"
            class="link"
            >Read more</a
          >.
        </p>
      </div>
      <bg-field
        label="Test type"
        directions="Please select the type of test that you want to create. Read more about the differences between Essential and Professional tests <a target='_blank' href='https://braingineers.crunch.help/faq/essential-professional-tests'>here</a>."
        name="name"
        v-if="activeContract"
      >
        <div class="select-holder">
          <bg-big-checkbox
            class="select-item"
            label="Essential"
            :disabled="isDisabled"
            :description="
              `(${activeContract.available_tests.essential} tests remaining)`
            "
            @change="
              test.test_type === 'essential'
                ? (test.test_type = null)
                : (test.test_type = 'essential')
            "
            :value="test.test_type === 'essential'"
          >
            <bg-icon
              :class="
                `brain-image ${
                  test.test_type === 'essential' ? 'brain-image--active' : null
                }`
              "
              name="allTests"
            />
          </bg-big-checkbox>
          <bg-big-checkbox
            @change="
              test.test_type === 'professional'
                ? (test.test_type = null)
                : (test.test_type = 'professional')
            "
            :value="test.test_type === 'professional'"
            :disabled="isDisabled"
            label="Professional"
            :description="
              `(${activeContract.available_tests.professional} tests remaining)`
            "
            class="select-item"
          >
            <bg-icon class="brain-image" name="brainrocket" />
          </bg-big-checkbox>
        </div>
      </bg-field>
      <div v-if="!isLoading && test.test_type !== null" class="input-holder">
        <bg-banner
          :isOpen="!popup[test.test_type]"
          :title="
            `0 remaining ${test.test_type.replace(/^\w/, (c) =>
              c.toUpperCase()
            )} tests`
          "
          :text="warningMessage"
        />
        <ValidationProvider
          ref="name"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="What are we calling it? :) Make sure you include your specific topic in the title"
            label="Test name"
            name="name"
            :error="errors && errors[0]"
          >
            <bg-input
              placeholder="Example: Redesign homepage"
              :error="errors && errors[0]"
              :disabled="isDisabled"
              v-model="test.name"
            />
          </bg-field>
        </ValidationProvider>
        <ValidationProvider
          ref="purpose"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="What do you hope to get out of this test?"
            label="Purpose of the test"
            name="purpose"
            :error="errors && errors[0]"
          >
            <bg-input
              placeholder="Example: You redesigned your homepage because your quantitative data showed several issues on this page. Therefore, you want to test the emotional experience of the redesign."
              :disabled="isDisabled"
              :error="errors && errors[0]"
              tag="textarea"
              v-model="test.purpose"
              :rows="6"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          v-if="test.test_type === 'professional'"
          directions="What are the most important elements you would like to gain insights into?"
          label="Areas of focus"
          name="focus_elements"
        >
          <bg-input
            class="mb"
            placeholder="Example: Banner on homepage"
            :disabled="isDisabled"
            v-model="test.focus_elements[0]"
          />
          <bg-input
            class="mb"
            placeholder="Example: Filter module"
            :disabled="isDisabled"
            v-model="test.focus_elements[1]"
          />
          <bg-input
            class="mb"
            placeholder="Example: Menu"
            :disabled="isDisabled"
            v-model="test.focus_elements[2]"
          />
        </bg-field>
        <ValidationProvider
          v-if="test.test_type === 'essential'"
          ref="focus_elements"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="What are the most important elements you would like to gain insights into?"
            label="Area of focus"
            name="focus_elements"
            v-if="test.test_type === 'essential'"
          >
            <bg-input
              :disabled="isDisabled"
              placeholder="Example: Filter module"
              v-model="test.focus_elements[0]"
            ></bg-input>
          </bg-field>
        </ValidationProvider>
        <ValidationProvider
          ref="scenario"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="Please describe the task that the participants need to carry out"
            label="Scenario"
            name="scenario"
            :error="errors && errors[0]"
          >
            <bg-input
              :error="errors && errors[0]"
              placeholder="Example: The participant should check the available options for a mobile subscription and find one he or she likes. When having found that subscription, they can order this subscription by going through checkout."
              :disabled="isDisabled"
              tag="textarea"
              v-model="test.scenario"
              :rows="6"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          directions="Please provide the link where the test should start."
          label="Start URL"
          name="url"
        >
          <bg-input
            :disabled="isDisabled"
            v-model="test.url"
            placeholder="Example: https://braingineers.com/product-tour/"
          ></bg-input>
        </bg-field>
        <ValidationProvider
          ref="stop_moment"
          rules="required"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions="Please describe at which point in the flow the test should end."
            label="End point"
            name="stop_moment"
            :error="errors && errors[0]"
          >
            <bg-input
              placeholder="Example: When a user is done filling in the payment details without actually placing the order."
              :error="errors && errors[0]"
              :disabled="isDisabled"
              v-model="test.stop_moment"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          label="Test device"
          directions="Please select the test device."
          name="name"
        >
          <div class="select-holder">
            <bg-big-checkbox
              class="select-item"
              label="Desktop"
              :disabled="isDisabled"
              @change="test.platform = 'desktop'"
              :value="test.platform === 'desktop'"
            >
              <img
                :src="images.desktop"
                class="img"
                alt="desktop-placeholder"
              />
            </bg-big-checkbox>
            <bg-big-checkbox
              @change="test.platform = 'mobile'"
              :value="test.platform === 'mobile'"
              :disabled="isDisabled"
              label="Mobile"
              class="select-item"
              image="mobile"
            >
              <img :src="images.mobile" class="img" alt="desktop-placeholder" />
            </bg-big-checkbox>
          </div>
        </bg-field>
        <bg-field
          label="Target group"
          directions="Please select the target group."
          name="target_group"
        >
          <div class="select-holder">
            <bg-big-checkbox
              description="Dutch representative sample with equal distribution in age, gender, and education level"
              :disabled="isDisabled"
              @change="test.target_group = 'default'"
              :value="test.target_group === 'default'"
              class="select-item"
              label="Default"
            >
              <bg-icon class="brain-image" name="targetGroup" />
            </bg-big-checkbox>
            <bg-big-checkbox
              description="Please provide additional criteria for participants recruitment.  Additional costs will apply."
              :disabled="isDisabled"
              @change="test.target_group = 'custom'"
              :value="test.target_group === 'custom'"
              label="Custom"
            >
              <bg-icon class="brain-image" name="targetGroupCustom" />
            </bg-big-checkbox>
          </div>
        </bg-field>
        <ValidationProvider
          ref="target_group"
          :rules="test.target_group === 'custom' ? 'required' : ''"
          v-slot="{ errors, valid, changed }"
        >
          <bg-field
            directions=" "
            label="Custom group details"
            name="target_group"
            :error="errors && errors[0]"
            v-if="test.target_group === 'custom'"
          >
            <bg-input
              placeholder="Participants with a mortgage, females only, dog owners, etc. Please specify down below. The target group and costs will be discussed during the kickoff call."
              :error="errors && errors[0]"
              :disabled="isDisabled"
              v-model="test.custom_target"
              tag="textarea"
              :rows="3"
            />
          </bg-field>
        </ValidationProvider>
        <bg-field
          directions="Don't worry if you're not 100% set. Our consultants will give you a call when you click 'request' to advice you and finalize your test setup."
          label="Ready?"
        ></bg-field>
        <div class="action-container">
          <div v-if="isEdit">
            <bg-button
              v-if="test.state === 'draft'"
              variant="destructive"
              class="action"
              text="Delete"
              @click="deleteTest"
            />
            <bg-button
              variant="secondary"
              :isLoading="isSavingDraft"
              class="action"
              text="Save as draft"
              @click="saveAsDraft"
            />
            <bg-button
              v-if="popup[test.test_type]"
              :isLoading="isRequestingTest"
              class="action"
              @click="saveAsRequest"
              text="Request test"
            />
          </div>
          <div v-if="!isEdit && !isView">
            <bg-button
              class="action"
              variant="secondary"
              text="Save as draft"
              :isLoading="isSavingDraft"
              @click="saveAsDraft"
            />
            <bg-button
              v-if="popup[test.test_type]"
              :isLoading="isRequestingTest"
              class="action"
              @click="saveAsRequest"
              text="Request test"
            />
          </div>
        </div>
      </div>
      <bg-loader :dark="true" v-if="isLoading"></bg-loader>
    </ValidationObserver>
  </div>
</template>

<style lang="scss" scoped>
.input-holder {
  max-width: 800px;
}
.select-holder {
  display: flex;
}

.select-item {
  margin-right: 20px;
}
.action-container {
  display: flex;
  margin-bottom: 200px;
}
.action {
  margin-right: 20px;
}
.pick-icon {
  height: 45px;
  width: 45px;
  margin-bottom: 1rem;
}
.brain-image {
  height: 90px;
  width: 90px;
  margin-bottom: 20px;
}

.brain-image--active {
  color: white;
}
.mb {
  margin-bottom: 10px;
}

.img {
  margin-bottom: -65px;
}

.link {
  color: var(--bg-color-text-link);
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BgBigCheckbox from "@/components/bg-big-checkbox";
import BgButton from "@/components/bg-button";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgLoader from "@/components/bg-loader";
import BgFilterBubble from "@/components/bg-filter-bubble";
import BgSplashCard from "@/components/bg-splash-card";
import BgBanner from "@/components/bg-banner";
import BgIcon from "@/components/bg-icon";
import { mapState } from "vuex";
import API from "@/api";

export default {
  name: "BgTests",
  components: {
    BgSelect,
    BgField,
    BgInput,
    BgBigCheckbox,
    BgButton,
    BgLoader,
    BgIcon,
    BgBanner,
  },
  directives: {
    BgTypography,
  },
  async created() {
    if (this.$route.params.id) {
      this.isLoading = true;
      const testData = await API.test.getTest(this.$route.params.id);
      if (testData.state !== "draft") {
        this.isDisabled = true;
        this.isView = true;
      } else {
        this.isEdit = true;
      }
      this.test = testData;
    }
    const companyData = await API.user.getCompany(
      this.userState.user.company.id
    );

    const activeContract = companyData.contracts.find(
      (contract) => contract.is_active === true
    );
    this.activeContract = activeContract;
    this.popup = {
      essential: activeContract.available_tests.essential > 0,
      professional: activeContract.available_tests.professional > 0,
    };
    if (!this.popup.essential && this.popup.professional) {
      this.warningMessage =
        "You have run out of Essential tests. You can Save this Essential test as a draft and one of our team members will get in touch with you for the next steps.";
    }
    if (this.popup.essential && !this.popup.professional) {
      this.warningMessage =
        "You have run out of Professional tests. You can Save this Professional test as a draft and one of our team members will get in touch with you for the next steps.";
    }
    this.isLoading = false;
  },
  methods: {
    saveAsRequest() {
      this.test.company_id = this.userState.user.company.id;
      this.isRequestingTest = true;
      this.$refs.observer.validate().then((res) => {
        if (res) {
          this.test.state = "requested";
          if (!this.test.updated_at) {
            API.test.createTest({ test: this.test }).then((res) => {
              this.$router.push({ path: `/success/` });
            });
          } else {
            API.test.updateTest({ test: this.test }).then((res) => {
              this.$router.push({ path: `/success/` });
            });
          }
        } else {
          this.isRequestingTest = false;
        }
      });
    },
    saveAsDraft() {
      this.isSavingDraft = true;
      this.test.state = "draft";
      this.test.company_id = this.userState.user.company.id;
      this.$refs.name.validate().then((res) => {
        if (res.valid) {
          if (!this.test.updated_at) {
            API.test.createTest({ test: this.test }).then((res) => {
              // this settimeout is because joan says reindexing takes literally infinite time, i'm sorry

              setTimeout(() => {
                this.$store.dispatch("fetchAllTests");
                this.$router.push({ path: `/tests/` });
              }, 1000);
            });
          } else {
            API.test.updateTest({ test: this.test }).then((res) => {
              setTimeout(() => {
                this.$store.dispatch("fetchAllTests");
                this.$router.push({ path: `/tests/` });
              }, 1000);
            });
          }
        } else {
          this.isSavingDraft = false;
        }
      });
    },
    deleteTest() {
      API.test.deleteTest(this.test.id).then(async (res) => {
        await this.$store.dispatch("fetchAllTests");
        this.$router.push({ path: `/tests/` });
      });
    },
    handlePopupClose(test_type) {
      return () => {
        this.popup[test_type] = true;
      };
    },
  },
  computed: {
    ...mapState({
      userState: (state) => state.user,
    }),
  },
  data() {
    return {
      isLoading: true,
      isEdit: false,
      isView: false,
      isDisabled: false,
      isRequestingTest: false,
      isSavingDraft: false,
      popupNotClosed: true,
      warningMessage: null,
      activeContract: null,
      popup: {
        essential: null,
        professional: null,
      },
      test: {
        target_group: null,
        platform: null,
        test_type: null,
        translation: "English",
        focus_elements: ["", "", ""],
        company_id: 0,
        target_group: "default",
        platform: "desktop",
      },
      images: {
        mobile: require("@/assets/imgs/placeholder_mobile.svg"),
        desktop: require("@/assets/imgs/placeholder_desktop.svg"),
      },
    };
  },
  mounted() {
    this.test.requester_id = this.userState.user.id;
  },
};
</script>
